/**
 * Inline SVG glyphs for the form controls.
 *
 * The legacy app leans on Material Design Icons (`mdi-eye`, `mdi-magnify`, …)
 * via Vuetify. There is no icon package here and adding one for five glyphs
 * would not survive the bundle-cost question in .claude/rules/performance.md,
 * so these are hand-written, currentColor-driven and tree-shakeable.
 *
 * All are decorative by default (aria-hidden) — the interactive wrapper owns
 * the accessible name.
 */
const base = {
  width: '1em',
  height: '1em',
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': 'true',
  focusable: 'false',
};

/** mdi-eye equivalent — password is currently visible. */
export function EyeIcon(props) {
  return (
    <svg {...base} {...props}>
      <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

/** mdi-eye-off equivalent — password is currently hidden. */
export function EyeOffIcon(props) {
  return (
    <svg {...base} {...props}>
      <path d="M9.9 4.24A9.1 9.1 0 0 1 12 4c6.4 0 10 7 10 7a18 18 0 0 1-2.16 3.19M6.6 6.6A18 18 0 0 0 2 11s3.6 7 10 7a9 9 0 0 0 5.4-1.6" />
      <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
      <path d="m2 2 20 20" />
    </svg>
  );
}

/** mdi-close — the clearable affordance. */
export function ClearIcon(props) {
  return (
    <svg {...base} {...props}>
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}

/** mdi-menu-down — the dropdown trigger chevron. */
export function ChevronDownIcon(props) {
  return (
    <svg {...base} {...props}>
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

/** mdi-check — the selected-option tick. */
export function CheckIcon(props) {
  return (
    <svg {...base} {...props}>
      <path d="m20 6-11 11-5-5" />
    </svg>
  );
}

/** mdi-content-save — the Save action in a page action bar. */
export function SaveIcon(props) {
  return (
    <svg {...base} {...props}>
      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2Z" />
      <path d="M17 21v-8H7v8M7 3v5h8" />
    </svg>
  );
}

/** mdi-delete-outline — the Delete Portfolio action. */
export function DeleteIcon(props) {
  return (
    <svg {...base} {...props}>
      <path d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6M10 11v6M14 11v6" />
    </svg>
  );
}

/** mdi-file-replace — Find and Replace on the Add instance card. */
export function FindReplaceIcon(props) {
  return (
    <svg {...base} {...props}>
      <path d="M14 3H6a2 2 0 0 0-2 2v6M14 3l5 5v3M14 3v5h5" />
      <path d="M3 17h8m-3-3 3 3-3 3M21 14h-6v6h6v-6Z" />
    </svg>
  );
}

/** mdi-magnify — the search field in a searchable dropdown. */
export function SearchIcon(props) {
  return (
    <svg {...base} {...props}>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </svg>
  );
}

/*
 * Filled glyphs for the app bar's action cluster.
 *
 * Separate base from the stroked icons above: legacy uses Material Design
 * Icons' FILLED variants here, which read as solid shapes rather than outlines.
 * Path data is MDI's own, so the silhouettes match the old tool exactly.
 *
 * Play and account are the PLAIN variants (mdi-play, mdi-account), not the
 * -circle ones: the white disc behind them is drawn by the button, so a
 * circular glyph would put a circle inside a circle.
 */
const solid = {
  width: '1em',
  height: '1em',
  viewBox: '0 0 24 24',
  fill: 'currentColor',
  'aria-hidden': 'true',
  focusable: 'false',
};

/** mdi-message-draw — the Feedback action. */
export function FeedbackIcon(props) {
  return (
    <svg {...solid} {...props}>
      <path d="M18 14h-7.5l2-2H18m-12 2v-2.5l6.88-6.86c.19-.19.51-.19.71 0l1.76 1.77c.2.2.2.51 0 .71L8.47 14M20 2H4a2 2 0 0 0-2 2v18l4-4h14a2 2 0 0 0 2-2V4c0-1.11-.9-2-2-2Z" />
    </svg>
  );
}

/** mdi-play — opens the product demo. The circle comes from the button. */
export function PlayIcon(props) {
  return (
    <svg {...solid} {...props}>
      <path d="M8 5.14v14l11-7-11-7Z" />
    </svg>
  );
}

/** mdi-bell — notifications. */
export function BellIcon(props) {
  return (
    <svg {...solid} {...props}>
      <path d="M21 19v1H3v-1l2-2v-6c0-3.1 2.03-5.83 5-6.71V4a2 2 0 0 1 4 0v.29c2.97.88 5 3.61 5 6.71v6l2 2m-7 2a2 2 0 0 1-4 0" />
    </svg>
  );
}

/** mdi-headset — support. */
export function HeadsetIcon(props) {
  return (
    <svg {...solid} {...props}>
      <path d="M12 1C7 1 3 5 3 10v7a3 3 0 0 0 3 3h3v-8H5v-2a7 7 0 0 1 7-7 7 7 0 0 1 7 7v2h-4v8h4v1h-7v2h6a3 3 0 0 0 3-3V10c0-5-4.03-9-9-9Z" />
    </svg>
  );
}

/** mdi-arrow-left — back to the previous page. */
export function ArrowLeftIcon(props) {
  return (
    <svg {...solid} {...props}>
      <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2Z" />
    </svg>
  );
}

/** mdi-help — the support page's heading badge. */
export function HelpIcon(props) {
  return (
    <svg {...solid} {...props}>
      <path d="M10 19h3v3h-3v-3m2-17c4.97 0 8 2.58 8 6 0 2.23-1.33 3.52-2.62 4.6C16.1 13.67 15 14.6 15 16h-3c0-2.5 1.17-3.56 2.4-4.6C15.43 10.53 16.3 9.8 16.3 8c0-1.84-1.72-3-4.3-3-2.41 0-4.3 1.05-4.3 3H5c0-3.42 3.03-6 7-6Z" />
    </svg>
  );
}

/** mdi-phone — a hotline number. */
export function PhoneIcon(props) {
  return (
    <svg {...solid} {...props}>
      <path d="M6.62 10.79a15.05 15.05 0 0 0 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57a1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2Z" />
    </svg>
  );
}

/** mdi-email — the support mailbox. */
export function EmailIcon(props) {
  return (
    <svg {...solid} {...props}>
      <path d="M20 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2m0 4-8 5-8-5V6l8 5 8-5v2Z" />
    </svg>
  );
}

/** mdi-map-marker — the region a hotline serves. */
export function MapMarkerIcon(props) {
  return (
    <svg {...solid} {...props}>
      <path d="M12 2a7 7 0 0 0-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 0 0-7-7m0 9.5A2.5 2.5 0 1 1 12 6.5a2.5 2.5 0 0 1 0 5Z" />
    </svg>
  );
}

/** mdi-open-in-new — trailing marker on an action that leaves the app. */
export function OpenInNewIcon(props) {
  return (
    <svg {...solid} {...props}>
      <path d="M14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3m-2 16H5V5h7V3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7h-2v7Z" />
    </svg>
  );
}

/** mdi-account — the signed-in user. The circle comes from the button. */
export function AccountIcon(props) {
  return (
    <svg {...solid} {...props}>
      <path d="M12 4a4 4 0 0 1 4 4 4 4 0 0 1-4 4 4 4 0 0 1-4-4 4 4 0 0 1 4-4m0 10c4.42 0 8 1.79 8 4v2H4v-2c0-2.21 3.58-4 8-4Z" />
    </svg>
  );
}

/**
 * fa-solid fa-key equivalent — marks a cloud-credential portfolio in the
 * sidebar list (one synced from a cloud account rather than uploaded).
 */
export function KeyIcon(props) {
  return (
    <svg {...base} {...props}>
      <circle cx="7.5" cy="15.5" r="4.5" />
      <path d="m10.7 12.3 9.8-9.8M17 6l3 3M14.5 8.5l2 2" />
    </svg>
  );
}

/** mdi-clock-outline — a telemetry portfolio waiting to be read. */
export function ClockIcon(props) {
  return (
    <svg {...base} {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}

/** mdi-refresh equivalent — check a big portfolio's progress again. */
export function RefreshIcon(props) {
  return (
    <svg {...base} {...props}>
      <path d="M20 11a8 8 0 1 0-2.3 5.7" />
      <path d="M20 4v7h-7" />
    </svg>
  );
}

/**
 * mdi-reload-alert equivalent — the same action, for a big portfolio whose work
 * has not started yet (status TO_PROCESS). The "!" is what tells the two apart.
 */
export function RefreshAlertIcon(props) {
  return (
    <svg {...base} {...props}>
      <path d="M20 11a8 8 0 1 0-2.3 5.7" />
      <path d="M20 4v7h-7" />
      <path d="M12 8v4M12 15.5v.01" />
    </svg>
  );
}

/** mdi-chevron-left equivalent — close the sidebar. */
export function ChevronLeftIcon(props) {
  return (
    <svg {...base} {...props}>
      <path d="m15 18-6-6 6-6" />
    </svg>
  );
}

/** mdi-chevron-right equivalent — open the sidebar again. */
export function ChevronRightIcon(props) {
  return (
    <svg {...base} {...props}>
      <path d="m9 18 6-6-6-6" />
    </svg>
  );
}

/** mdi-chip equivalent — the Regions page's "CPU Generations" toggle. */
export function ChipIcon(props) {
  return (
    <svg {...base} {...props}>
      <rect x="6" y="6" width="12" height="12" rx="2" />
      <rect x="9.5" y="9.5" width="5" height="5" rx="1" />
      <path d="M9 2v4M15 2v4M9 18v4M15 18v4M2 9h4M2 15h4M18 9h4M18 15h4" />
    </svg>
  );
}

/** mdi-arrow-right equivalent — a link that goes somewhere ("List of AWS Regions →"). */
export function ArrowRightIcon(props) {
  return (
    <svg {...base} {...props}>
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  );
}

/** mdi-chevron-up equivalent — the "increase" half of a number stepper. */
export function ChevronUpIcon(props) {
  return (
    <svg {...base} {...props}>
      <path d="m18 15-6-6-6 6" />
    </svg>
  );
}

/** mdi-percent equivalent — the Contract Discount button. */
export function PercentIcon(props) {
  return (
    <svg {...base} {...props}>
      <path d="M19 5 5 19" />
      <circle cx="6.5" cy="6.5" r="2.5" />
      <circle cx="17.5" cy="17.5" r="2.5" />
    </svg>
  );
}

/** mdi-upload equivalent — Upload File. */
export function UploadIcon(props) {
  return (
    <svg {...base} {...props}>
      <path d="M12 15V4M7 9l5-5 5 5" />
      <path d="M4 15v4a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-4" />
    </svg>
  );
}

/** mdi-cloud-upload-outline equivalent — Upload File in the empty instance list. */
export function CloudUploadIcon(props) {
  return (
    <svg {...base} {...props}>
      <path d="M7 18a4.5 4.5 0 0 1-.6-8.96A6 6 0 0 1 18 9.5a4 4 0 0 1 0 8.5h-1" />
      <path d="M12 20v-8M9 15l3-3 3 3" />
    </svg>
  );
}

/** mdi-server equivalent — the empty instance list. */
export function ServerIcon(props) {
  return (
    <svg {...base} {...props}>
      <rect x="4" y="4" width="16" height="7" rx="2" />
      <rect x="4" y="13" width="16" height="7" rx="2" />
    </svg>
  );
}

/** mdi-plus equivalent — Add Instance. */
export function PlusIcon(props) {
  return (
    <svg {...base} {...props}>
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

/** mdi-pencil equivalent — start editing a value in place (Contract Discount%). */
export function PencilIcon(props) {
  return (
    <svg {...base} {...props}>
      <path d="M4 20h4L19 9l-4-4L4 16v4ZM13.5 6.5l4 4" />
    </svg>
  );
}

/** mdi-chart-bar equivalent — the Analytics view. */
export function ChartBarIcon(props) {
  return (
    <svg {...base} {...props}>
      <path d="M4 20h16M7 16v-5M12 16V6M17 16v-8" />
    </svg>
  );
}

/** mdi-view-module equivalent — the Card view. */
export function ViewModuleIcon(props) {
  return (
    <svg {...base} {...props}>
      <rect x="4" y="4" width="7" height="7" rx="1" />
      <rect x="13" y="4" width="7" height="7" rx="1" />
      <rect x="4" y="13" width="7" height="7" rx="1" />
      <rect x="13" y="13" width="7" height="7" rx="1" />
    </svg>
  );
}

/** mdi-view-list equivalent — the Table view. */
export function ViewListIcon(props) {
  return (
    <svg {...base} {...props}>
      <path d="M9 6h11M9 12h11M9 18h11M4 6h.01M4 12h.01M4 18h.01" />
    </svg>
  );
}

/** mdi-download equivalent — save a file (PPT, Export, Invalid Records). */
export function DownloadIcon(props) {
  return (
    <svg {...base} {...props}>
      <path d="M12 4v11M7 10l5 5 5-5" />
      <path d="M4 19h16" />
    </svg>
  );
}

/** mdi-shield-check equivalent — DID-verified reports. */
export function ShieldCheckIcon(props) {
  return (
    <svg {...base} {...props}>
      <path d="M12 3 5 6v5c0 4.5 3 8.3 7 10 4-1.7 7-5.5 7-10V6l-7-3Z" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  );
}

/** mdi-information-outline equivalent — a section's help button. */
export function InfoIcon(props) {
  return (
    <svg {...base} {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5M12 8h.01" />
    </svg>
  );
}
